import React from 'react'
import PropTypes from 'prop-types'
import styled from '@emotion/styled'

import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos'
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos'

import { getPirStepperArray, isPirPaymentEnabled } from '../../util/pirConsts.js'
import pirTerminology from '../../util/pirTerminology'
import { Divider, SpaceBetweenRowContainer, FlexEndButtonErrorContainer, FlexColumn, MedBGContainer, LightBGContainer } from '../styles/Containers'
import { BoldFormTitle, BoldBodyText, BoldSmallBodyText } from '../styles/Text'
import { ErrorMessage, InfoMessage } from '../common/UserMessage.js'
import Button, { SecondaryButton } from '../common/Button'
import DetailSection from '../common/DetailSection.js'
import BrowserFile from '../files/BrowserFile.js'
import Carousel from '../common/Carousel.js'

const SectionContainer = styled(LightBGContainer)`
  display: flex;
  flex-direction: column;
  gap: 15px;
  padding: 15px;
  flex: 1 1 0px;
  min-width: 200px;
`

const FlexRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 15px 2%;
  justify-content: space-between;
`

const EditButton = styled(SecondaryButton)`
  @media only screen and (max-width: 600px) {
    order: 1;
    align-self: flex-start;
  }
`

const PayButtonContainer = styled(FlexEndButtonErrorContainer)`
  @media only screen and (max-width: 600px) {
    order: 2;
    width: 50%;
    align-self: flex-start;
  }
`

const TierOptionItem = styled.div`
  font-size: 12px;
  text-wrap: wrap;
  word-break: break-word;
  margin-bottom: 0;
  line-height: 1.5;
`

const TierLabel = styled(BoldSmallBodyText)`
  width: 40%;

  @media only screen and (max-width: 600px) {
    width: 100%;
  }
`

const TierValueContainer = styled.div`
  width: 60%;
  display: flex;
  flex-direction: column;
  gap: 2px;

  @media only screen and (max-width: 600px) {
    width: 100%;
  }
`

const BoldTierName = styled.span`
  font-weight: 600;
  font-size: 12px;
  text-transform: capitalize;
`

function renderDetails(section, details, signature) {
  const keysWithSubchildren = Object.keys(details).filter(d => typeof details[d] === 'object' && !Array.isArray(details[d]))
  const detailsWithoutSubchildren = Object.keys(details).reduce((acc, d) => {
    if (typeof details[d] !== 'object' || Array.isArray(details[d])) acc[d] = details[d]
    return acc
  }, {})

  return (
    <SectionContainer>
      <BoldBodyText>{section}</BoldBodyText>
      <Divider />
      {Object.keys(detailsWithoutSubchildren).length > 0 && <DetailSection details={detailsWithoutSubchildren} />}
      {keysWithSubchildren.length > 0 && keysWithSubchildren.map(d => <DetailSection key={d} sectionLabel={d} details={details[d]} />)}
      {signature && (
        <MedBGContainer padding="0">
          <BrowserFile file={signature} showButton={false} />
        </MedBGContainer>
      )}
    </SectionContainer>
  )
}

function renderRequestInformation(requestInformation) {
  const selectedTier = requestInformation?.['Selected Tier']
  const additionalDetails = requestInformation?.['Additional Details']
  const hasTier = Boolean(selectedTier)
  const hasAdditionalDetails = Boolean(additionalDetails)

  if (!hasTier && !hasAdditionalDetails) return null

  const tierOptions = requestInformation.tierOptions || []

  return (
    <SectionContainer>
      <BoldBodyText>Request Information</BoldBodyText>
      <Divider />
      {hasTier && (
        <SpaceBetweenRowContainer gap="5px 15px" style={{ alignItems: 'flex-start' }}>
          <TierLabel>Selected Tier</TierLabel>
          <TierValueContainer>
            <BoldTierName>{selectedTier}</BoldTierName>
            {tierOptions.length > 0 && (
              <FlexColumn gap="2px">
                {tierOptions.map((option, index) => (
                  <TierOptionItem key={index}>{option}</TierOptionItem>
                ))}
              </FlexColumn>
            )}
          </TierValueContainer>
        </SpaceBetweenRowContainer>
      )}
      {hasAdditionalDetails && (
        <FlexColumn gap="10px" style={{ marginTop: '15px' }}>
          <DetailSection details={{ 'Additional Details': additionalDetails }} />
        </FlexColumn>
      )}
    </SectionContainer>
  )
}

function submissionErrorMessage(submissionError, fileTypeErrorMessage) {
  if (submissionError === 'fileType') return fileTypeErrorMessage
  if (submissionError === 'owner' || submissionError === 'address') {
    return `The entered ${submissionError} does not match our records. Please update your request and try again.`
  }
  if (submissionError === 'signatureUpload') return 'The owner signature could not be uploaded. Please try again.'
  if (submissionError === 'signatureDispatch') return 'Electronic signatures could not be sent. Please try again.'
  return `An error occurred while submitting the ${pirTerminology.getRequestShortName()}. Please try again.`
}

function PIRReview({
  pir,
  onBack = () => {},
  onNext = () => {},
  submissionLoading = false,
  submissionError,
  ownerMatches = true,
  fileTypeErrorMessage,
  editDisabled = false
}) {
  const paymentEnabled = isPirPaymentEnabled()

  return (
    <FlexColumn gap="30px">
      <SpaceBetweenRowContainer gap="50px">
        <EditButton onClick={onBack} padding="large" disabled={submissionLoading || editDisabled}>
          <ArrowBackIosIcon />
          Edit
        </EditButton>
        <Carousel steps={getPirStepperArray(paymentEnabled)} activeStep={1} />
        <PayButtonContainer>
          <Button onClick={onNext} padding="large" loading={submissionLoading}>
            {paymentEnabled ? 'Pay' : 'Submit'}
            <ArrowForwardIosIcon />
          </Button>
          {submissionError && <ErrorMessage>{submissionErrorMessage(submissionError, fileTypeErrorMessage)}</ErrorMessage>}
        </PayButtonContainer>
      </SpaceBetweenRowContainer>
      {!ownerMatches && (
        <InfoMessage>The entered owner does not match the owner on record. Please double check the owner is correct before proceeding.</InfoMessage>
      )}
      <MedBGContainer padding="30px">
        <FlexColumn gap="15px">
          <BoldFormTitle>Request Details</BoldFormTitle>
          {renderDetails('Property Information', pir.propertyInformation)}
          <FlexRow>
            {renderDetails('Owner Information', pir.ownerInformation, pir.ownerSignature)}
            {pir.strataInformation && renderDetails(pirTerminology.getAssociationLabel('Information'), pir.strataInformation)}
            {renderDetails('Requestor Information', pir.requestorInformation)}
          </FlexRow>
          {pir.requestInformation && renderRequestInformation(pir.requestInformation)}
        </FlexColumn>
      </MedBGContainer>
    </FlexColumn>
  )
}

PIRReview.propTypes = {
  pir: PropTypes.object.isRequired,
  onBack: PropTypes.func,
  onNext: PropTypes.func,
  submissionLoading: PropTypes.bool,
  submissionError: PropTypes.string,
  ownerMatches: PropTypes.bool,
  fileTypeErrorMessage: PropTypes.string,
  editDisabled: PropTypes.bool
}

export default PIRReview
